import { Attachment, generateAttachmentsPayload } from '@/helpers/generateAttachmentsPayload';
import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { UserRequestDraft, UserService } from '@/api/types';

import { CitizenAppealMergedSchema } from '../model/types';

interface Props {
  requestId: UserRequestDraft['id'];
  formData: CitizenAppealMergedSchema;
  service: UserService;
  serviceFileManager?: ServiceFileManager;
}

export const generatePayload = ({
  requestId,
  service,
  formData,
  serviceFileManager,
}: Props): {
  commonPayload: typeof commonPayload;
  signedPayload: typeof signedPayload;
} => {
  const attachments: Attachment[] = generateAttachmentsPayload({
    userCategory: formData.userCategory,
    appealAttachments: formData.appealAttachments,
  });

  const applicationFileId = serviceFileManager?.getAttachment()?.fileId;
  if (applicationFileId) {
    attachments.push({
      formField: 'application',
      files: [applicationFileId],
    });
  }

  const commonPayload = {
    requestId: +requestId,
    service: {
      id: service.id,
      name: service.name,
      code: service.code,
    },
    user: {
      first_name: formData.firstName,
      middle_name: formData.middleName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone,
      category: {
        id: formData.category?.id,
        confirmation_required: formData.category?.confirmation_required,
        name: formData.category?.label,
      },
    },
    address: {
      country_id: formData.residenceAddress.country.id,
      country_name: formData.residenceAddress.country.label,
      region_id: formData.residenceAddress.region.id,
      region_name: formData.residenceAddress.region.label,
      district_id: formData.residenceAddress.district?.id || null,
      district_name: formData.residenceAddress.district?.label || null,
      settlement_id: formData.residenceAddress.settlement.id,
      settlement_name: formData.residenceAddress.settlement.label,
      street_id: formData.residenceAddress.street.id,
      street_name: formData.residenceAddress.street.label,
      house: formData.residenceAddress.house,
      building: formData.residenceAddress.building,
      apartment: formData.residenceAddress.apartment,
      zip_code: formData.residenceAddress.zip_code,
    },
    appeal: {
      kind: {
        id: formData.appealType.id,
        name: formData.appealType.label,
      },
      description: formData.appealMessage,
    },
    attachments,
  };

  const signedPayload = {
    ...commonPayload,
    ...(serviceFileManager?.getSignature() || {}),
  };

  return {
    commonPayload,
    signedPayload,
  };
};
